import browser from 'browser-detect';
import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    of,
    Subject,
    Subscription,
    zip
} from 'rxjs';
import { Title, Meta } from '@angular/platform-browser';
import { environment as env } from '../../environments/environment';
import { Router, NavigationEnd, Navigation } from '@angular/router';
import { LgvAPIService } from '../core/services/lgv-api.service';
import { HttpClient } from '@angular/common/http';
import { LgvAuthMgrService } from '../core/services/lgv-auth-mgr.service';
import { register } from 'swiper/element/bundle';

import {
    routeAnimations,
    NotificationService,
    LgvConstants,
    LgvModuleItem,
    LgvModuleService,
    LgvNavItem,
    LgvSettingsService,
    LgvClientService,
    LgvLocalStorageService,
    LgvTSHelper
} from '../core/core.module';
import {
    combineLatestWith,
    filter,
    first,
    map,
    mergeMap,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { LgvUserService } from '../core/services/lgv-user.service';
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { LgvModel } from '../data-models/longevity.model';
import { StdObj } from '../data-models/standard-objects';
import { MatDialog } from '@angular/material/dialog';
import { select } from '@ngrx/store';
import { LgvNavBuilderService } from '../core/services/lgv-nav-builder.service';
import { NavDisplayService } from '../core/services/nav-display.service';
import { LgvCompanyService } from '../core/services/lgv-company.service';
import { LgvPropertyService } from '../core/services/lgv-property.service';
import { EditClientProfileComponent } from '../shared/edit-client-profile/edit-client-profile.component';
import { LgvUrlService } from '../core/services/lgv-url.service';
import { JsonLoaderService } from '../core/services/json-loader.service';
import { LgvAnalyticsService } from '../core/services/lgv-analytics.service';
import { SplashDialogComponent } from '../shared/splash-dialog/splash-dialog.component';

register();

@Component({
    selector: 'lus-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [routeAnimations]
})
export class AppComponent implements OnInit, OnDestroy {
    isProd = env.production;
    envName = env.envName;
    version = env.versions.app;
    year = new Date().getFullYear();

    pagesForActiveModule = [];
    pagesForSideMenu = [];
    ActiveModule: LgvModuleItem = null;

    get IsDefaultModule(): boolean {
        return this.ActiveModule && this.ActiveModule.id === 'default';
    }

    favIcon: HTMLLinkElement = document.querySelector('#appFavIcon');

    navigationSideMenu$ = of([]);

    DebugInfo: StdObj.DebugInfo = {
        numMyHomeClicks: 0,
        lastPageClick: '',
        numRouterEvents: 0,
        numSetActiveModuleEvents: 0
    };

    //stickyHeader$: Observable<boolean>;
    //theme$: Observable<string>;

    UserIsAuthenticated$: Observable<boolean>;
    //AuthUser$: Observable<any>;

    urlKey = 'Lgv_OrigURL';
    get OrigURL(): string {
        return localStorage.getItem(this.urlKey);
    }
    set OrigURL(x: string) {
        localStorage.setItem(this.urlKey, x);
    }

    lastLoginKey = 'Lgv_lastLogin';
    get LastLogin(): string {
        return localStorage.getItem(this.lastLoginKey);
    }
    set LastLogin(x: string) {
        localStorage.setItem(this.lastLoginKey, x);
    }

    get IsClientMode(): boolean {
        return this.ActiveModule && this.ActiveModule.id === 'clientView';
    }
    get IsAgentMode(): boolean {
        return this.ActiveModule && this.ActiveModule.id === 'agentView';
    }

    ClientLoginMode = false;
    ClientLoginForm: UntypedFormGroup;
    get ClientEmailField() {
        return this.ClientLoginForm.get('email');
    }
    ClientEmail: string;

    _ClientInvitationRequestStatus = 'None';
    ClientInvitationRequestStatus$ = new BehaviorSubject(
        this._ClientInvitationRequestStatus
    );

    _HaveExpiredToken = false;
    HaveExpiredToken$ = new BehaviorSubject(this._HaveExpiredToken);

    ClientInfo: LgvModel.ClientInfo;
    OfflineMode = false;
    onDestroy$ = new Subject();

    companySubscription: Subscription;

    showHeaderFooter$ = new BehaviorSubject(true);

    sideNavMode$ = new BehaviorSubject('wide');
    get isNarrowSideNav$(): Observable<boolean> {
        return this.sideNavMode$.pipe(
            takeUntil(this.onDestroy$),
            map(x => x === 'narrow')
        );
    }
    get isWideSideNav$(): Observable<boolean> {
        return this.sideNavMode$.pipe(
            takeUntil(this.onDestroy$),
            map(x => x === 'wide')
        );
    }

    recentlyUploadedDocs: any[] = [];

    hiddenRoutes = [
        'csr-external',
        'external-client-registration',
        'qp',
        'chatbot',
        'chatbot-container',
        'awr',
        'avgRatesHistoric',
        'avgRates',
        'avgCompare',
        'monthlyCost'
    ];

    constructor(
        private lgvLocalStorageSvc: LgvLocalStorageService,
        public authMgr: LgvAuthMgrService,
        public LgvModuleSvc: LgvModuleService,
        public lgvCompanySvc: LgvCompanyService,
        private lgvPropertySvc: LgvPropertyService,
        private titleService: Title,
        private metaService: Meta,
        public LgvApiSvc: LgvAPIService,
        public LgvUserSvc: LgvUserService,
        public LgvClientSvc: LgvClientService,
        private analytics: LgvAnalyticsService,
        private http: HttpClient,
        public MatDialog: MatDialog,
        private FB: UntypedFormBuilder,
        private router: Router,
        public LgvSettings: LgvSettingsService,
        private readonly notificationService: NotificationService,
        private lgvNavBuilderSvc: LgvNavBuilderService,
        private navDisplayService: NavDisplayService,
        private urlService: LgvUrlService,
        private jsonLoader: JsonLoaderService
    ) {
        this.OfflineMode = env.offlineMode;

        let subscription = combineLatest([
            this.lgvCompanySvc.activeCompany$,
            this.lgvCompanySvc.activeCompanyBranding$
        ])
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(([c, b]) => {
                if (!!c && !!b) {
                    //console.log('========  Have Company and Branding ============');
                    //console.log('company');
                    //console.log(c);
                    //console.log('branding');
                    //console.log(b);

                    const imgUrl = `${c.baseUrl}/assets/images/logos/${c.companyCode}-logo-small.png`;
                    this.metaService.updateTag({
                        property: 'og:image',
                        content: imgUrl
                    });
                    this.metaService.updateTag({
                        property: 'og:title',
                        content: `${c.appName} by xxx ${c.name}`
                    });
                    this.metaService.updateTag({
                        property: 'og:url',
                        content: c.baseUrl
                    });

                    let favIconUrl = this.lgvCompanySvc.getBrandingAsset(
                        lgvCompanySvc.faviconKey,
                        b.assets,
                        true
                    );
                    if (favIconUrl) {
                        this.favIcon.href = favIconUrl;
                    }
                }
            });
    }

    private static isIEorEdgeOrSafari() {
        return ['ie', 'edge', 'safari'].includes(browser().name);
    }

    ngOnInit(): void {
        //console.log('+++ Calling setActiveModule from NgOnInit() +++');
        this.setActiveModule('/');
        this.jsonLoader.initializeJsonConfigs();

        this.lgvCompanySvc.activeCompany$
            .pipe(
                first(f => f !== null),
                takeUntil(this.onDestroy$)
            )
            .subscribe(c => {
                (<any>window)?.clientGA('create', c.googleTrackingId, 'auto');
            });

        (<any>window)?.rfGA('config', 'UA-198467374-3', {
            send_page_view: false
        });

        this.lgvLocalStorageSvc.testLocalStorage();

        this.authMgr.IsAuthenticated$.pipe(
            filter(f => f === true),
            takeUntil(this.onDestroy$)
        ).subscribe(x => {
            if (
                !this.authMgr.IsSecureClient &&
                !this.authMgr.secureClientTokenRecentlyExpried
            ) {
                this.initAfterAuth();
            }
        });

        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                takeUntil(this.onDestroy$),
                withLatestFrom(this.urlService.previousUrl$)
            )
            .subscribe(([event, prevUrl]) => {
                // if (this.LgvUserSvc.HaveDBUserSS
                //     && (event as NavigationEnd)?.url?.includes('admin/client-details')
                //     && prevUrl.includes('client/my-home')) {
                //     location.reload();
                // } else {
                //     this.onNavigationEvent(event as NavigationEnd);
                // }

                this.onNavigationEvent(event as NavigationEnd);
            });

        if (!this.OrigURL || !this.OrigURL.includes('token')) {
            //console.log('@@@@@@@@@@ Checking for saved Bearer Token @@@@@@@@@@');
            this.authMgr.CheckForSavedBearerToken();
        }

        this.ClientLoginForm = this.FB.group({
            email: [null, [Validators.required, Validators.email]]
        });

        this.authMgr.ClientExpiredTokenError$.pipe(
            takeUntil(this.onDestroy$)
        ).subscribe(x => {
            if (x) {
                if (x.error.client !== null) {
                    //console.log('Have client in error');
                    this.ClientInfo = x.error.client as LgvModel.ClientInfo;
                    this.HaveExpiredToken$.next(true);
                }
            } else {
                this.HaveExpiredToken$.next(false);
            }
        });

        this.initSideNavState();
    }

    ngOnDestroy(): void {
        this.onDestroy$.complete();

        if (this.companySubscription) {
            this.companySubscription.unsubscribe();
        }
    }

    initAfterAuth() {
        this.UserIsAuthenticated$ = this.authMgr.IsAuthenticated$;

        //console.log(`+++++++++++ initAfterAuth()  |  authMgr.AuthState$: ${this.authMgr.AuthState$.value} +++++++++++`)

        if (this.authMgr.IsClient) {
            this.LgvUserSvc.LoadClientAgent();
        } else {
            if (
                this.authMgr.Auth0UserInfo$.value &&
                !this.authMgr.Auth0UserInfo$.value.email_verified
            ) {
                this.notificationService.warn('Email is not verified');
            } else {
                this.LgvUserSvc.LoadDBUserSS(true);
                zip([
                    this.LgvUserSvc.DBUserSS$.pipe(first(f => f !== null)),
                    this.lgvCompanySvc.loanApplications$.pipe(
                        first(f => f !== null)
                    )
                ]).subscribe(([user, loanApp]) => {
                    this.navigationSideMenu$ = of(
                        this.LgvModuleSvc.NavPagesForRoles(
                            this.LgvUserSvc.DBUserRoles
                        ).map(nav => {
                            return { ...nav, link: 'admin/' + nav.link };
                        })
                    );
                });
            }
        }

        let redirectUrl = '';
        const modName = this.LgvModuleSvc.ModFromLink(this.OrigURL);
        const hasToken = this.OrigURL && this.OrigURL.includes('/token/');
        const paramValue = LgvTSHelper.getParamValueFromURL(
            'param',
            this.OrigURL
        );

        //console.log(`hasToken: ${hasToken}  |  paramValue: ${paramValue}  | OrigUrl: ${this.OrigURL}`);

        if (hasToken && !!paramValue) {
            switch (paramValue) {
                case 'TaskItems': {
                    this.OrigURL = '/';
                    this.LgvClientSvc.IsSSReady$.pipe(
                        filter(f => f !== null && f === true),
                        first()
                    ).subscribe(() => {
                        this.router.navigate(['/client/loan-app']);
                    });
                    break;
                }
            }
        } else if (
            this.OrigURL &&
            this.OrigURL.length > 1 &&
            !this.OrigURL.includes('default') &&
            !this.OrigURL.includes('/token/') &&
            modName != null
        ) {
            redirectUrl = this.OrigURL.replace(
                /(^\w+:|^)\/\/(\w.+)\/#/gm,
                ''
            ).replace('/#/', '/');
            this.OrigURL = '/';
            this.setActiveModule(redirectUrl);
            this.router.navigate([redirectUrl]);
        } else {
            // navigate to homepage only if not using home dashboard as an external link
            if (this.showHeaderFooter$.value && !this.hiddenRoutes.some(route => this.OrigURL.includes(route))) {
                this.LgvApiSvc.navigateToHomePage();
            }
        }
    }

    onNavigationEvent(event: NavigationEnd) {
        const firstPath = event.url.split('/')[1];
        const secondPath = event.url.split('/')[2];
        const shouldShowNav =
            this.navDisplayService.shouldShowNav(firstPath) &&
            this.navDisplayService.shouldShowNav(secondPath);
        this.showHeaderFooter$.next(shouldShowNav);

        // Set sidebar to narrow state when viewing email page
        if (firstPath === 'admin' && secondPath === 'view-email') {
            this.setSideNavState('narrow');
        }

        const isClientModule = firstPath === 'client';
        if (isClientModule) {
            this.navigationSideMenu$ = this.lgvNavBuilderSvc.clientNavigationSideMenu$;
        } else {
            if (this.LgvUserSvc.HaveDBUserSS) {
                this.lgvCompanySvc.loanApplications$
                    .pipe(first(f => f !== null))
                    .subscribe(loanApp => {
                        this.navigationSideMenu$ = of(
                            this.LgvModuleSvc.NavPagesForRoles(
                                this.LgvUserSvc.DBUserRoles
                            ).map(nav => {
                                return {
                                    ...nav,
                                    link: firstPath + '/' + nav.link
                                };
                            })
                        );
                    });
            }
        }

        this.setActiveModule(event.url, event);

        try {
            if (!!this.showHeaderFooter$.value) {
                document.getElementById('header').scrollIntoView();
            }
        } catch (err) {}

        // Track page view using analytics service
        this.analytics.trackPageView(event.urlAfterRedirects);
    }

    setActiveModule(url: string, event: NavigationEnd = null) {
        this.DebugInfo.numSetActiveModuleEvents++;

        //console.log('Looking for mod in setActiveModule - ' + url);
        const mod = this.LgvModuleSvc.ModFromLink(url);

        if (mod) {
            if (!this.ActiveModule || this.ActiveModule.link !== mod.link) {
                //console.log('Setting as active mod');

                this.ActiveModule = mod;
                this.pagesForActiveModule = this.ActiveModule.childPages.filter(
                    x => !x.hidden
                );
                this.pagesForSideMenu = [
                    ...this.pagesForActiveModule,
                    { link: 'settings', label: 'Settings' }
                ];
            }

            const navItem: LgvNavItem = this.updatePageTitle(url, mod);

            if (
                navItem &&
                event &&
                event.id > 2 &&
                this.authMgr.IsClient &&
                url.indexOf('token') == -1
            ) {
                /*
                 * exclude the first 2 navigations [event.id > 2] which are automatic (/default and /client/my-home)
                 * also exclude the token view page
                 */

                /*
                 * Some clients are triggering "clusters" of My Home page clicks.  Suppress client activity and document as a "Debug Event" to help debug what is causing it.
                 */

                let shouldSuppressEvent = false;
                if (navItem.label === 'My Home') {
                    this.DebugInfo.numMyHomeClicks =
                        this.DebugInfo.lastPageClick === 'My Home'
                            ? this.DebugInfo.numMyHomeClicks + 1
                            : 1;

                    if (this.DebugInfo.numMyHomeClicks > 1) {
                        console.log('***  Suppressing My Home Click ***');
                        this.LgvClientSvc.PostDebugEvent(
                            `Supressed My Home Click => numMHClicks: ${this.DebugInfo.numMyHomeClicks}  |  numRouter: ${this.DebugInfo.numRouterEvents}  |  numSetMod: ${this.DebugInfo.numSetActiveModuleEvents}`
                        )
                            .pipe(first())
                            .subscribe();
                        shouldSuppressEvent = true;
                    }
                }

                if (!shouldSuppressEvent) {
                    this.LgvClientSvc.PostPageClick(navItem.label)
                        .pipe(first())
                        .subscribe();
                }
            }
        }
    }

    updatePageTitle(url: string, mod: LgvModuleItem): LgvNavItem {
        //console.log('updatePageTitle - Url: ' + url + '  Mod: ' + mod.label);

        let company = this.lgvCompanySvc.activeCompany$.value;
        const companyAndAppName = !!company
            ? `${company.name} - ${company.appName ?? 'HomeDashboard'}`
            : '';

        const navItem = LgvModuleItem.NavItemFromLink(mod, url);
        const title = navItem
            ? `${navItem.label} | ${companyAndAppName}`
            : null;
        this.titleService.setTitle(title ? title : companyAndAppName);

        return navItem;
    }

    onLogoClicked() {
        // Only navigate if we're in a primary module context
        // This ensures widget URLs are preserved
        if (env.offlineMode) {
            return;
        }

        this.authMgr.IsAuthenticated$.pipe(
            first(f => f !== null),
            takeUntil(this.onDestroy$)
        ).subscribe(() => {
            // Only navigate if we're not already on a default page
            if (this.showHeaderFooter$.value && this.router.url !== '/') {
                this.LgvApiSvc.navigateToHomePage();
            }
        });
    }

    onHomeClicked() {
        if (env.offlineMode) {
            return;
        }

        this.authMgr.IsAuthenticated$.pipe(
            first(f => f !== null),
            takeUntil(this.onDestroy$)
        ).subscribe(() => {
            // Only navigate if we're not already on a default page
            if (this.showHeaderFooter$.value && this.router.url !== '/') {
                this.LgvApiSvc.navigateToHomePage();
            }
        });
    }

    onChangePassword() {
        this.authMgr.Auth0UserInfo$.pipe(takeUntil(this.onDestroy$)).subscribe(
            p => {
                const a0Email = p.email as string;

                const domainBase = env.Auth0Config.domain;
                const url = `https://${domainBase}/dbconnections/change_password`;

                const requestOptions = {
                    responseType: 'text'
                } as Object;

                const body = {
                    client_id: '', //cmpConfig.clientID,  kxk: todo  ***  Not sure how this ever worked.  Can't find where clientID for company config was being set
                    email: a0Email,
                    connection: 'Username-Password-Authentication'
                };

                this.http
                    .post(url, body, requestOptions)
                    .pipe(first())
                    .subscribe({
                        next: () => {
                            this.notificationService.info(
                                `An email has been sent to ${a0Email} with instructions on how to reset your password.`
                            );
                        },
                        error: () => {
                            this.notificationService.error(
                                'Unable to send reset password email.'
                            );
                        }
                    });
            }
        );
    }

    //kxk: todo Need to fix changeSkin() when ready
    //changeSkin(code: string) {
    //    this.LgvCompanySvc.SetCompany(code, true);
    //}

    onToggleShowCoBranding(args: any) {
        this.LgvSettings.SetShowCoBranding(args.checked);
    }

    impersonateThisAgentAsAssistant(agentID: string) {
        this.router.navigate([`/admin/view-agent/${agentID}`]);
    }

    onOpenEditClientProfileModal(clientInfo: LgvModel.ClientInfo) {
        const dialogRef = this.MatDialog.open(EditClientProfileComponent, {
            width: '600px',
            panelClass: 'dialog-container',
            disableClose: true,
            data: { clientInfo: null }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    // Refresh snapshot
                    this.LgvClientSvc.LoadSnapshotForNonClient(
                        this.LgvClientSvc.ClientID$.value,
                        false,
                        true
                    );
                }
            });
    }

    onGoToCompanyProfile() {
        this.router.navigate([
            `/admin/company/${this.LgvUserSvc.AgentSS$.value?.company?.id}`
        ]);
    }

    //#region Generate Client Data

    onGenerateSampleBatchData() {
        this.LgvUserSvc.AgentDTO$.pipe(first()).subscribe(agentDTO => {
            if (!!!agentDTO) {
                this.notificationService.warn(
                    'Please select an agent and try again.'
                );
                return;
            }

            let clientSuffix: number = Math.round(Math.random() * 10000);
            let ownerId = agentDTO.agentInfo.id;

            let batchData = [
                {
                    clientBody: {
                        firstName: 'John',
                        lastName: 'Doe',
                        ownerId: '',
                        email: `John.Doe.${clientSuffix}@seed.data`,
                        mobilePhone: 9255195761
                    },
                    propertyBody: {
                        address: '152 ESTHER LN',
                        city: 'DANVILLE',
                        state: 'CA',
                        zip: '94526',
                        county: 'CONTRA COSTA',
                        propertyAssociationTypeId:
                            'AB372ABD-ABAF-EC11-8CC9-000C290D53A0',
                        listingPrice: 1,
                        primaryProperty: true,
                        propertyValuationId: '10186177'
                    }
                },
                {
                    clientBody: {
                        firstName: 'Jane',
                        lastName: 'Doe',
                        ownerId: '',
                        email: `Jane.Doe.${clientSuffix}@seed.data`,
                        mobilePhone: 9257832259
                    },
                    propertyBody: {
                        address: '173 VIA SERENA',
                        city: 'ALAMO',
                        state: 'CA',
                        zip: '94507',
                        county: 'CONTRA COSTA',
                        propertyAssociationTypeId:
                            'AB372ABD-ABAF-EC11-8CC9-000C290D53A0',
                        listingPrice: 1,
                        primaryProperty: true,
                        propertyValuationId: '10185183'
                    }
                },
                {
                    clientBody: {
                        firstName: 'Mary',
                        lastName: 'Smith',
                        ownerId: '',
                        email: `Mary.Smith.${clientSuffix}@seed.data`,
                        mobilePhone: 9255568468
                    },
                    propertyBody: {
                        address: '473 WALNUT AVE',
                        city: 'WALNUT CREEK',
                        state: 'CA',
                        zip: '94598',
                        county: 'CONTRA COSTA',
                        propertyAssociationTypeId:
                            'AB372ABD-ABAF-EC11-8CC9-000C290D53A0',
                        listingPrice: 1,
                        primaryProperty: true,
                        propertyValuationId: '10125292'
                    }
                },
                {
                    clientBody: {
                        firstName: 'Dan',
                        lastName: 'Aurbach',
                        ownerId: '',
                        email: `Dan.Aurbach.${clientSuffix}@seed.data`,
                        mobilePhone: 938569637
                    },
                    propertyBody: {
                        address: '5742 MORAGA AVE',
                        city: 'PIEDMONT',
                        state: 'CA',
                        zip: '94611',
                        county: 'ALAMEDA',
                        propertyAssociationTypeId:
                            'AB372ABD-ABAF-EC11-8CC9-000C290D53A0',
                        listingPrice: 1,
                        primaryProperty: true,
                        propertyValuationId: '9479564'
                    }
                }
            ];

            for (let b of batchData) {
                this.onGenerateSampleClientData(
                    ownerId,
                    b.clientBody,
                    b.propertyBody
                );
            }
        });
    }

    onGenerateSampleClientData(
        ownerId: string,
        clientBody: any,
        propertyBody: any
    ) {
        console.log('Sample Data:');

        clientBody.ownerId = ownerId;
        const clientId = '';

        //this.isLoading$.next(true);

        this.LgvClientSvc.SaveClient(clientBody, clientId)
            .pipe(
                takeUntil(this.onDestroy$),
                mergeMap(response => {
                    return zip(
                        this.lgvPropertySvc.addClientProperty(
                            response.id,
                            propertyBody
                        ),
                        of(response)
                    );
                }),
                mergeMap(([propertyResponse, clientResponse]) => {
                    return zip(
                        of(propertyResponse),
                        of(clientResponse),
                        this.LgvApiSvc.FetchPublicRecordReportByID(
                            propertyResponse.id,
                            clientResponse.id
                        )
                    );
                })
            )
            .subscribe(
                ([propertyResponse, clientResponse, publicRecordResponse]) => {
                    if (propertyResponse) {
                        this.notificationService.success(
                            `${clientBody.firstName} ${clientBody.lastName} has been added.`
                        );

                        /*
                    /user/client/activities throws a 403 error when trying to post activities as a non-client
                */

                        //let activityDate = new Date();
                        //activityDate.setDate(activityDate.getDate() - 10);
                        //this.PostFakeClientActivity('BEB73295-A8CA-EA11-8B03-000D3A3280F4', activityDate).subscribe();
                    }
                },
                err => {
                    //this.isLoading$.next(false);
                    this.notificationService.error(
                        `There was an error adding ${clientBody.firstName} ${clientBody.lastName}`
                    );
                    console.log(err);
                }
            );
    }

    PostFakeClientActivity(
        activityTypeID: string,
        time: Date,
        notes: string = null
    ): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/user/client/activities`;
        let model = {
            appInstanceId: this.LgvClientSvc.AppInstanceID,
            activityTypeId: activityTypeID,
            timestamp: LgvTSHelper.jsDateToJsonDate(time),
            notes: notes
        };

        return this.http.post<LgvModel.Activity>(bldUrl, model).pipe(first());
    }

    //#endregion Generate Client Data

    //#region Login Stuff
    onSubmitClientEmail() {
        this.ClientEmail = this.ClientEmailField.value;
        this.sendNewInvitationEmail();
    }

    sendNewInvitationEmail() {
        this.LgvClientSvc.sendNewClientInvitationEmail(this.ClientEmail)
            .pipe(first())
            .subscribe({
                next: () => {
                    this.ClientInvitationRequestStatus$.next('Success');
                },
                error: () => this.ClientInvitationRequestStatus$.next('Error')
            });
    }

    onLoginMenuClosed() {
        this.resetClientLoginState();
    }

    resetClientLoginState() {
        this.ClientLoginMode = false;
        this.ClientInvitationRequestStatus$.next('None');
        this.ClientEmailField.setValue('');
    }

    onRefreshToken() {
        if (this.ClientInfo && this.ClientInfo.email) {
            this.ClientEmail = this.ClientInfo.email;
            this.sendNewInvitationEmail();
        }
    }

    //#endregion Login Stuff

    //#region Side Nav Folder State
    initSideNavState() {
        // Default to narrow on desktop
        if (window.innerWidth >= 992) {
            this.setSideNavState('narrow');
        } else {
            let val = localStorage.getItem('lgv.side-nav-panel');
            if (val && val.length > 0) {
                this.sideNavMode$.next(val);
            }
        }
    }

    setSideNavState(mode: string) {
        this.sideNavMode$.next(mode);
        // Only store state for mobile
        if (window.innerWidth < 992) {
            localStorage.setItem('lgv.side-nav-panel', mode);
        }
    }

    // Keep this for mobile only
    onToggleSideNavWidth() {
        if (window.innerWidth < 992) {
            this.setSideNavState(
                this.sideNavMode$.value === 'wide' ? 'narrow' : 'wide'
            );
        }
    }
    //#endregion Side Nav Folder State

    cssForLongString(str: string): string {
        return str?.length > 25 ? 'small-text' : '';
    }
}
